import { useCallback, useRef, useState } from 'react';
import PropTypes from 'prop-types';

import './RangeSlider.scss';

// How many decimals a value has to keep to sit exactly on the step, so a pointer-driven value
// reads back as 0.3 rather than 0.30000000000000004
const decimalsOf = (step) => {
  const [, decimals = ''] = String(step).split('.');
  return decimals.length;
};

/**
 * A range input drawn as one tall progress bar: the travelled part is filled and the label sits
 * centred on top of it, so the control is a single pill rather than a bar with captions stacked
 * around it. The fill edge is the handle — there is no knob to aim at, you drag the bar itself.
 *
 * The fill is a layer of its own stacked over the input rather than a stop in the input's own
 * background gradient, because a gradient is the one way of drawing it that cannot be animated:
 * WebKit does not interpolate background-image at all. Clipping a solid colour animates
 * everywhere, and that clip is what gives the bar its settle.
 */
const RangeSlider = ({
  id,
  label,
  ariaLabel,
  value,
  onChange,
  min = 0,
  max = 1,
  step = 0.01,
  formatValue,
  disabled = false,
}) => {
  const inputRef = useRef(null);
  // null when idle, 'press' from the touch that starts a drag, 'track' once the finger is
  // actually moving. The two live states differ only in how the fill catches up — see the SCSS.
  const [drag, setDrag] = useState(null);

  // Drives both the track's gradient and where the label flips colour, so the two can't drift
  const fill = max > min ? ((value - min) / (max - min)) * 100 : 0;

  // iOS Safari never starts a drag from a press on the track — only a press that lands on the
  // thumb moves the value, and the thumb here is an invisible band at the fill edge, so anywhere
  // else the bar looks dead. Driving the value from the pointer ourselves makes the whole bar
  // draggable, the way it already is on every other engine.
  const setValueFromPointer = useCallback(
    (clientX) => {
      const input = inputRef.current;
      if (!input) return;

      const { left, width } = input.getBoundingClientRect();
      if (!width) return;

      const ratio = Math.min(Math.max((clientX - left) / width, 0), 1);
      const raw = min + ratio * (max - min);
      const stepped = step > 0 ? min + Math.round((raw - min) / step) * step : raw;
      const next = Number(Math.min(Math.max(stepped, min), max).toFixed(decimalsOf(step)));
      if (next === Number(input.value)) return;

      // React reads the value it last saw off the node itself, so a plain assignment would look
      // like no change at all. Writing through the prototype's setter bypasses that record, and
      // the dispatched event then reaches onChange as a real edit would.
      const setValue = Object.getOwnPropertyDescriptor(window.HTMLInputElement.prototype, 'value').set;
      setValue.call(input, String(next));
      input.dispatchEvent(new Event('input', { bubbles: true }));
    },
    [min, max, step],
  );

  const handlePointerDown = (event) => {
    if (disabled) return;
    // Capture keeps the moves coming to the input once the finger leaves the bar, so a drag that
    // strays off the pill vertically still tracks
    event.currentTarget.setPointerCapture(event.pointerId);
    setDrag('press');
    setValueFromPointer(event.clientX);
  };

  const handlePointerMove = (event) => {
    if (disabled || !event.currentTarget.hasPointerCapture(event.pointerId)) return;
    // A press lands wherever it lands, and that jump gets the full settle; only once the finger
    // is travelling does the fill switch to sitting under it
    setDrag('track');
    setValueFromPointer(event.clientX);
  };

  // Fires for a lift and for a cancel alike, so the bar can never be left mid-drag
  const handleDragEnd = () => setDrag(null);

  const text = (
    <>
      {label}
      {formatValue && <span className="value">{formatValue(value)}</span>}
    </>
  );

  return (
    <div
      className={`RangeSlider${drag ? ' dragging' : ''}${drag === 'track' ? ' tracking' : ''}`}
      style={{ '--fill': `${fill}%` }}
    >
      <input
        id={id}
        ref={inputRef}
        className="track"
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={onChange}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onLostPointerCapture={handleDragEnd}
        disabled={disabled}
        aria-label={ariaLabel}
      />
      {/* Sits over the input and is clipped to the value, so the drag has to pass through it */}
      <span className="fill" aria-hidden="true" />
      {label && (
        <label className="label" htmlFor={id}>
          <span className="text">{text}</span>
          {/* The same words again, clipped to the filled part, so the label switches to dark
              exactly where its background turns pale */}
          <span className="text text--over" aria-hidden="true">
            {text}
          </span>
        </label>
      )}
    </div>
  );
};

RangeSlider.propTypes = {
  id: PropTypes.string.isRequired,
  label: PropTypes.node,
  // What the control is called, for a label that carries no words of its own
  ariaLabel: PropTypes.string,
  value: PropTypes.number.isRequired,
  onChange: PropTypes.func.isRequired,
  min: PropTypes.number,
  max: PropTypes.number,
  step: PropTypes.number,
  formatValue: PropTypes.func,
  disabled: PropTypes.bool,
};

export default RangeSlider;
